import React, { PropsWithChildren, useRef } from 'react';

import * as RadixDialog from '@radix-ui/react-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import IconClose from '@repo/ui-icons/IconClose';
import cn from 'classnames';

import { utilityClasses } from '../../../css/utils';
import { popupSafeClassName, isPopupSafeElement } from '../../../helpers';
import { CircleButton } from '../Button';

import styles from './Dialog.module.css';
import type { DialogContentProps, DialogRootProps } from './types';

const DialogRoot: React.FC<PropsWithChildren<DialogRootProps>> = ({
  children,
  open,
  onOpenChange,
  modal = false,
  dataTestId = 'modal--root',
}) => {
  return (
    <RadixDialog.Root
      data-testid={dataTestId}
      modal={modal}
      open={open}
      onOpenChange={onOpenChange}
    >
      {children}
    </RadixDialog.Root>
  );
};

const DialogTrigger: React.FC<PropsWithChildren> = ({ children }) => {
  return <RadixDialog.Trigger asChild>{children}</RadixDialog.Trigger>;
};

const DialogContent: React.FC<PropsWithChildren<DialogContentProps>> = ({
  dataTestId = 'modal--content',
  title,
  children,
  height,
  width,
  minHeight,
  minWidth,
  onInteractOutside,
  onEscapeKeyDown,
  showCloseButton = true,
  global = false,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Prevent closing dialog with click outside if focus is outside the dialog
  // This is needed to prevent closing dialog when focusing elements/interacting with elements on another (global) dialog
  const checkInteractOutsideEventTarget = (
    e: Parameters<
      NonNullable<RadixDialog.DialogContentProps['onInteractOutside']>
    >[0]
  ) => {
    if (e.target !== overlayRef.current && isPopupSafeElement(e.target)) {
      e.preventDefault();
    } else {
      onInteractOutside?.(e);
    }
  };

  // Prevent closing dialog with pointer down outside if focus is outside the dialog
  // This is needed to prevent closing dialog when focusing elements/interacting with elements on another (global) dialog
  const checkPointerDownOutsideEventTarget = (
    e: Parameters<
      NonNullable<RadixDialog.DialogContentProps['onPointerDownOutside']>
    >[0]
  ) => {
    if (e.target !== overlayRef.current && isPopupSafeElement(e.target)) {
      e.preventDefault();
    }
  };

  const checkEscapeKeyTarget = (e: KeyboardEvent) => {
    // Prevent closing dialog with Escape key if focus is outside the dialog (e.g. on another dialog)
    if (
      e.target instanceof HTMLElement &&
      !contentRef.current?.contains(e.target)
    ) {
      e.preventDefault();
    } else {
      onEscapeKeyDown?.(e);
    }
  };

  return (
    <RadixDialog.Portal>
      <RadixDialog.Overlay
        ref={overlayRef}
        className={cn(styles.overlay, global && popupSafeClassName)}
      />

      <RadixDialog.Content
        ref={contentRef}
        aria-describedby={undefined} // We are not supporting description yet
        className={cn(
          styles['content-container'],
          global && popupSafeClassName
        )}
        data-testid={dataTestId}
        style={{ height, width, minHeight, minWidth }}
        onEscapeKeyDown={checkEscapeKeyTarget}
        onInteractOutside={checkInteractOutsideEventTarget}
        onPointerDownOutside={checkPointerDownOutsideEventTarget}
      >
        <VisuallyHidden.Root>
          <RadixDialog.Title>{title}</RadixDialog.Title>
        </VisuallyHidden.Root>
        <div className={styles.content}>{children}</div>
        {showCloseButton && (
          <div className={styles['dynamic-size-wrapper']}>
            <RadixDialog.Close asChild>
              <CircleButton
                ariaLabel="close-dialog"
                className={cn(
                  styles['close-button'],
                  utilityClasses['center-children']
                )}
                dataTestId="modal--close-button"
                icon={IconClose}
                name="closeDialogButton"
                size="s"
                variant="secondary"
              />
            </RadixDialog.Close>
          </div>
        )}
      </RadixDialog.Content>
    </RadixDialog.Portal>
  );
};

DialogContent.displayName = 'DialogContent';

export const Dialog = {
  Root: DialogRoot,
  Trigger: DialogTrigger,
  Content: DialogContent,
};
